import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import {
  resolvePropertyOrder,
  type OrderablePropertyKey,
} from "#app/widgets/issue/issue-summary-stack";
import { Fragment, useMemo, useCallback, type ReactNode } from "react";
import {
  Box,
  PropertyGrid,
  PropertyRow,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorSelected,
  EntitySelectorPool,
  EntitySelectorEmpty,
  toastManager,
  type EntitySelectorConfig,
} from "@salestrak/ui";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import { useWorkflowStages } from "#app/lib/hooks/use-workflow-stages";
import {
  DETAIL_EDIT,
  useIssueViewMutations,
} from "#app/hooks/issue-view/use-issue-view-mutations";
import { canManageSharing } from "#app/lib/utils/access-level";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import type { IssueViewStage } from "./issue-view-types";
import type { IssueTypeWithFields } from "./issue-view-types";
import { IssueTeamsProperty } from "./issue-teams-property";
import type { EntityRefValue, IssueRow, StageOption } from "#app/lib/data";
import {
  WorkflowSelector,
  IssueTypeSelector,
  StageSelector,
  IssueSourceSelector,
  SelectionEntityTrigger,
  selectionToWorkflowEntity,
  selectionToUserEntity,
} from "#app/components/entity-selectors";
import { AddressValueCell } from "#app/components/fields/address-value-cell";
import { useIssueViewDialogState } from "./issue-view-dialog-state";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { removeIssueFromStageCache, insertIssueIntoStageCache } from "#app/hooks/kanban/kanban-cache-utils";
import { useUserPool, USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  assigneeToSelection,
  useAssigneeSelection,
} from "#app/components/entity-selectors/use-assignee-selection";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { SummaryPropertiesFoldRow, useSummaryEmptyFold } from "./summary-fold-row";
import { typeInHero } from "#app/lib/type-placement";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import type { Omittable } from "@salestrak/ui";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

// The omnibus `updateIssue` here only sets the source.
const PROPERTIES_EDIT = { ...DETAIL_EDIT, updateIssueLabel: "update_source" };

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type IssueViewPropertiesProps = Omittable<{
  issue?: IssueRow;
  stage?: IssueViewStage | null;
  workflowId?: string | null;
  address?: {
    id: string;
    addressId: string;
    wholeAddress?: string | null;
    latLng?: { lat: number; lng: number } | null;
  } | null;
  assignees?: EntityRefValue[];
  issueTypes?: IssueTypeWithFields[];
  /** The Type the row shows and changes — used only in the "property"
   *  placement (`typePlacement`); the hero owns it otherwise. */
  issueTypeId?: string | null;
  onIssueTypeChange?: (issueTypeId: string) => void;
}>;

export function IssueViewProperties({
  issue,
  stage,
  workflowId: freshWorkflowId,
  address,
  assignees,
  issueTypes,
  issueTypeId,
  onIssueTypeChange,
}: IssueViewPropertiesProps) {
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { settings: issueSourceSettings } = useIssueSourceSettings();
  // "off" removes the Source row from the band entirely — the workspace has
  // decided projects do not carry one. Stored values are kept, just not
  // shown, and a type's Source requirement stands down with the row.
  const sourceOff = issueSourceSettings.projectSourcePolicy === "off";

  const mutations = useIssueViewMutations(PROPERTIES_EDIT);
  // Pure-view access (or comment-only) → property mutations no-op.
  // Create mode bypasses the gate because the issue doesn't exist
  // yet; the parent dialog already gated entry into create mode by
  // role.
  const { canEdit, level } = useIssueViewCapabilities();
  const editableHere = isCreateMode || canEdit;
  // ClickUp's empty-property fold (see `useSummaryEmptyFold`): a viewer tucks
  // the rows that hold no value behind one "More" row and brings them back
  // from it. Type, Workflow and Stage always hold a value, so this section
  // can never fold to nothing.
  const emptyFold = useSummaryEmptyFold();
  const hideEmpty = !isCreateMode && emptyFold.collapsed;
  // The built-in properties the project's TYPE requires ("assignees",
  // "teams", "address", "source"): marked on the row, never folded, and — in
  // create mode — a blank one blocks Create like a required custom field does.
  const layoutTypeId = isCreateMode ? createCtx.draft.issueTypeId : issue?.issueTypeId;
  const layoutType = useMemo(
    () => issueTypes?.find((it) => it.issueTypeId === layoutTypeId) ?? null,
    [issueTypes, layoutTypeId],
  );
  const requiredProperties = useMemo(() => {
    const keys = isCreateMode ? createCtx.requiredProperties : (layoutType?.requiredProperties ?? []);
    return new Set(keys.filter((key) => key !== "source" || !sourceOff));
  }, [isCreateMode, createCtx, layoutType, sourceOff]);
  // The band's movable rows in the type's order (Type, Workflow, Stage lead).
  const propertyOrder = useMemo(
    () => resolvePropertyOrder(layoutType?.propertyOrder),
    [layoutType],
  );
  // Assignee adds run the same "they can't see this project" prompt as
  // @-mentions (handled inside useAssigneeSelection) — checked in the
  // background after the mutation fires, never blocking it.
  const viewerCanManageSharing = canManageSharing(level);
  // Wrap each mutation so a view/comment-tier viewer can't kick off
  // a write even if a callback fires. The backend rejects regardless,
  // but skipping the round-trip keeps the optimistic UI honest.
  const noop = useCallback(
    () => Promise.resolve({ data: undefined as never }) as never,
    [],
  );
  const { updateWorkflow, updateStage, updateIssue, updateAddress, updateAssignees } = editableHere
    ? mutations
    : {
        updateWorkflow: noop,
        updateStage: noop,
        updateIssue: noop,
        updateAddress: noop,
        updateAssignees: noop,
      };
  const { pool: userPool } = useUserPool();

  // In create mode, read from draft. In view mode, use props/issue.
  const currentWorkflowId = isCreateMode ? createCtx.draft.workflowId : (freshWorkflowId ?? issue?.workflowId ?? "");
  const currentIssueTypeId = isCreateMode
    ? createCtx.draft.issueTypeId
    : (issueTypeId ?? issue?.issueTypeId ?? "");
  const selectableIssueTypes =
    issueTypes && issueTypes.length > 0
      ? issueTypes
      : issue?.issueType
        ? [issue.issueType]
        : [];

  const { stages: workflowStages } = useWorkflowStages(isCreateMode ? undefined : currentWorkflowId);

  // Rows travel whole: the optimistic Stage below restates every column an
  // open widget reads (the pin pair included), so a projection here would
  // silently blank the destination Stage's cadence for a frame.
  const stageOptions = useMemo<StageOption[]>(() => {
    if (isCreateMode) return createCtx.stages;
    if (workflowStages.length > 0) return workflowStages;
    // While the full list loads, include the current stage so the trigger
    // can display the selected stage name immediately.
    if (stage) return [stage];
    return [];
  }, [isCreateMode, createCtx, workflowStages, stage]);

  const currentStageId = isCreateMode ? (createCtx.draft.stageId ?? "") : (stage?.stageId ?? issue?.stageId ?? "");

  const handleWorkflowSelect = useCallback(
    (workflowId: string) => {
      if (workflowId === currentWorkflowId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ workflowId, stageId: null });
        return;
      }

      if (!issue) return;
      updateWorkflow({
        variables: { issueId: issue.issueId, workflowId },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            workflowId,
            stageId: issue.stageId,
            stage: {
              __typename: "Stage" as const,
              id: stage?.id ?? "",
              stageId: stage?.stageId ?? issue.stageId,
              name: stage?.name ?? "",
              sortOrder: stage?.sortOrder ?? 0,
              // The real type and pin, not placeholders: cards and the update
              // widget resolve the effective cycle off this optimistic Stage
              // the moment the move paints, and a guessed `type` would flip a
              // closed Stage open and show it a cadence it does not have.
              type: stage?.type ?? null,
              isDefault: stage?.isDefault ?? false,
              workflowId,
              icon: {
                __typename: "Icon" as const,
                icon: stage?.icon?.icon ?? null,
                color: stage?.icon?.color ?? null,
                percentage: null,
              },
              updateCycle: stage?.updateCycle ?? null,
              updateCycleOff: stage?.updateCycleOff ?? false,
            },
          },
        },
        update: (cache) => {
          removeIssueFromStageCache(cache, currentStageId, issue.issueId);
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename !== "Issue") {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update workflow",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update workflow");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentWorkflowId, currentStageId, stage, updateWorkflow],
  );

  const handleStageSelect = useCallback(
    (stageId: string) => {
      if (stageId === currentStageId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ stageId });
        return;
      }

      if (!issue) return;
      const selectedStage = stageOptions.find((s) => s.stageId === stageId);
      updateStage({
        variables: { issueId: issue.issueId, stageId },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            stageId,
            stage: {
              __typename: "Stage" as const,
              id: selectedStage?.id ?? "",
              stageId,
              name: selectedStage?.name ?? "",
              sortOrder: selectedStage?.sortOrder ?? 0,
              type: selectedStage?.type ?? null,
              isDefault: selectedStage?.isDefault ?? false,
              workflowId: currentWorkflowId,
              icon: {
                __typename: "Icon" as const,
                icon: selectedStage?.icon?.icon ?? null,
                color: selectedStage?.icon?.color ?? null,
                percentage: null,
              },
              // The destination Stage's own pin, so the update widget resolves
              // the new cadence in the same frame the Stage chip changes.
              updateCycle: selectedStage?.updateCycle ?? null,
              updateCycleOff: selectedStage?.updateCycleOff ?? false,
            },
          },
        },
        update: (cache) => {
          removeIssueFromStageCache(cache, currentStageId, issue.issueId);
          insertIssueIntoStageCache(cache, stageId, {
            issueId: issue.issueId,
            sortOrder: Number.MIN_SAFE_INTEGER,
          });
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename !== "Issue") {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update stage",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update stage");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentStageId, currentWorkflowId, stageOptions, updateStage],
  );

  const currentIssueSourceId = isCreateMode
    ? (createCtx.draft.issueSourceId ?? "")
    : (issue?.issueSourceId ?? "");

  const handleIssueSourceSelect = useCallback(
    (issueSourceId: string) => {
      if (issueSourceId === currentIssueSourceId) return;

      if (isCreateMode) {
        createCtx.updateDraft({ issueSourceId });
        return;
      }

      if (!issue) return;
      // Matches issue-type changes: the source field is set via the generic
      // updateIssue mutation, which returns the full issue (with the resolved
      // issueSource) so Apollo updates the cache by id.
      updateIssue({
        variables: { issueId: issue.issueId, input: { issueSourceId } },
        onCompleted: (data) => {
          if (data.updateIssue.__typename !== "Issue") {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update source",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update source");
        },
      });
    },
    [isCreateMode, createCtx, issue, currentIssueSourceId, updateIssue],
  );

  const handleAddressSelect = useCallback(
    (wholeAddress: string) => {
      if (isCreateMode) {
        createCtx.updateDraft({ address: { wholeAddress } });
        return;
      }
      if (!issue) return;
      updateAddress({
        variables: { issueId: issue.issueId, address: { wholeAddress } },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            addressId: issue.addressId ?? null,
            address: {
              __typename: "Address" as const,
              id: address?.id ?? `optimistic-${issue.issueId}`,
              addressId: address?.addressId ?? `optimistic-${issue.issueId}`,
              wholeAddress,
            },
          },
        },
        onCompleted: (data) => {
          if (data.updateIssue.__typename !== "Issue") {
            toastManager.add({
              title:
                "message" in data.updateIssue
                  ? data.updateIssue.message
                  : "Failed to update address",
              type: "error",
            });
          }
        },
        onError: (err) => {
          toastMutationError(err, "Failed to update address");
        },
      });
    },
    [isCreateMode, createCtx, issue, address, updateAddress],
  );

  const handleAddressClear = useCallback(() => {
    if (isCreateMode) {
      createCtx.updateDraft({ address: null });
      return;
    }
    if (!issue) return;
    updateAddress({
      variables: { issueId: issue.issueId, address: null },
      optimisticResponse: {
        updateIssue: {
          __typename: "Issue" as const,
          id: issue.id,
          issueId: issue.issueId,
          addressId: null,
          address: null,
        },
      },
      onCompleted: (data) => {
        if (data.updateIssue.__typename !== "Issue") {
          toastManager.add({
            title:
              "message" in data.updateIssue
                ? data.updateIssue.message
                : "Failed to clear address",
            type: "error",
          });
        }
      },
      onError: (err) => {
        toastMutationError(err, "Failed to clear address");
      },
    });
  }, [isCreateMode, createCtx, issue, updateAddress]);

  // ---------------------------------------------------------------------------
  // Assignee entity-selector state (members-only, one changeable primary)
  // ---------------------------------------------------------------------------

  const assigneeInitialSelections = useMemo(() => {
    if (isCreateMode) return createCtx.draft.assignees;
    const poolMap = new Map(userPool.items.map((item) => [item.id, item]));
    // Defaulted inside the memo: an `?? []` fallback outside it is a new
    // array every render, and the memo would never hold.
    return (assignees ?? [])
      .filter((a) => a.user || poolMap.has(a.entityId))
      .map((a) => assigneeToSelection(a, poolMap));
  }, [isCreateMode, createCtx, assignees, userPool.items]);

  const assigneeConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [userPool],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
      noItemsMessage: "No members yet",
    }),
    [userPool],
  );

  const {
    draftSelections: assigneeDraftSelections,
    handleValueChange: handleAssigneeValueChange,
    handleOpenChange: handleAssigneeOpenChange,
    getSetPrimaryLabel,
    handleSetPrimary,
  } = useAssigneeSelection({
    initialSelections: assigneeInitialSelections,
    issue: isCreateMode || !issue ? null : { id: issue.id, issueId: issue.issueId },
    updateAssignees,
    viewerCanManageSharing,
    onDraftChange: isCreateMode
      ? (next) => createCtx.updateDraft({ assignees: next })
      : undefined,
  });

  const resolvedAddress = isCreateMode
    ? createCtx.draft.address
      ? { wholeAddress: createCtx.draft.address.wholeAddress }
      : undefined
    : address !== undefined
      ? address
      : issue?.address;

  const { desktopSize, setDesktopSize } = useIssueViewDialogState();
  const appNavigate = useAppNavigate();
  const focusLatLng = !isCreateMode ? (address?.latLng ?? null) : null;
  const focusAddressId = !isCreateMode ? address?.addressId : undefined;
  const handleShowOnMap = useCallback(() => {
    if (!focusLatLng || !issue?.issueId) return;
    if (desktopSize === "fullscreen") setDesktopSize("sidebar");
    const params = new URLSearchParams({
      lat: String(focusLatLng.lat),
      lng: String(focusLatLng.lng),
      z: "16",
      issueId: issue.issueId,
    });
    if (focusAddressId) params.set("addressId", focusAddressId);
    trackEvent({
      context: AnalyticsContext.Issue,
      event: IssueEvent.OpenAddressOnMap,
      data: { via: "detail_address", geocoded: false },
    });
    appNavigate.push(`/map?${params.toString()}`);
  }, [focusLatLng, focusAddressId, desktopSize, setDesktopSize, appNavigate, issue?.issueId]);

  const blank = {
    assignees: assigneeDraftSelections.length === 0,
    teams: (isCreateMode ? createCtx.draft.teams : (issue?.teams ?? [])).length === 0,
    address: !resolvedAddress?.wholeAddress,
    source: !currentIssueSourceId,
  };
  // Which rows the fold may hide. Type, Workflow and Stage always hold a
  // value; these four are the ones a project can leave blank — and a blank
  // row the type REQUIRES stays up, its emptiness being the signal.
  const emptyRow = {
    assignees: blank.assignees && !requiredProperties.has("assignees"),
    teams: blank.teams && !requiredProperties.has("teams"),
    // An "off" row never renders, so it must not count toward the fold either.
    source: !sourceOff && blank.source && !requiredProperties.has("source"),
    address: blank.address && !requiredProperties.has("address"),
  };
  const propertyError = (key: keyof typeof blank, message: string) =>
    createCtx?.submitted && requiredProperties.has(key) && blank[key] ? message : undefined;
  const emptyRowCount = Object.values(emptyRow).filter(Boolean).length;
  const foldable = !isCreateMode && emptyRowCount > 0;

  // The movable rows, keyed so the type's order can lay them out; each is
  // the same block it always was, fold gate included.
  const movableRows: Record<OrderablePropertyKey, ReactNode> = {
    assignees: (
      <>
            {hideEmpty && emptyRow.assignees ? null : (
            <PropertyRow
              icon={ASSIGNEE_ICON}
              label="Assignees"
              required={requiredProperties.has("assignees")}
              error={propertyError("assignees", "Assignees are required")}
              id="issue-assignees"
            >
              <EntitySelector
                title="Assignees"
                config={assigneeConfig}
                value={assigneeDraftSelections}
                onValueChange={handleAssigneeValueChange}
                onOpenChange={handleAssigneeOpenChange}
              >
                <SelectionEntityTrigger
                  toEntity={selectionToUserEntity}
                  placeholder="Add assignees..."
                  title="Assignees"
                  emptyTextVariant="subtle"
                  size="default"
                  readOnly={!editableHere}
                />
                <EntitySelectorContent size="card">
                  <EntitySelectorInput />
                  <EntitySelectorSelected
                    label="Assignees"
                    hideWhenEmpty
                    hideBadge
                    hideSelectionHighlight
                    getItemSecondaryActionLabel={getSetPrimaryLabel}
                    onItemSecondaryAction={handleSetPrimary}
                  />
                  <EntitySelectorPool value={USER_POOL_ID} hideSelectionHighlight />
                  <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
                </EntitySelectorContent>
              </EntitySelector>
            </PropertyRow>
            )}
  
      </>
    ),
    teams: (
      <>
            {hideEmpty && emptyRow.teams ? null : (
            <PropertyRow
              icon={PROPERTY_ICON.team}
              label="Teams"
              required={requiredProperties.has("teams")}
              error={propertyError("teams", "Teams are required")}
              id="issue-teams"
            >
              <IssueTeamsProperty
                issue={isCreateMode ? undefined : (issue ?? undefined)}
                editable={editableHere}
                viewerCanManageSharing={viewerCanManageSharing}
              />
            </PropertyRow>
            )}
  
      </>
    ),
    address: (
      <>
            {hideEmpty && emptyRow.address ? null : (
            <PropertyRow
              icon={PROPERTY_ICON.address}
              label="Address"
              required={requiredProperties.has("address")}
              error={propertyError("address", "Address is required")}
              id="issue-address"
            >
              <AddressValueCell
                wholeAddress={resolvedAddress?.wholeAddress}
                latLng={focusLatLng}
                addressId={focusAddressId}
                placeholder="Add an address..."
                emptyTextVariant="subtle"
                inset="property"
                onSelect={handleAddressSelect}
                onClear={handleAddressClear}
                onOpenInMap={focusLatLng ? handleShowOnMap : undefined}
                readOnly={!editableHere}
              />
            </PropertyRow>
            )}
  
      </>
    ),
    source: (
      <>
            {/* Source closes the band: provenance is set once and read rarely. */}
            {sourceOff || (hideEmpty && emptyRow.source) ? null : (
            <PropertyRow
              icon={PROPERTY_ICON.issueSource}
              label="Source"
              required={requiredProperties.has("source")}
              error={propertyError("source", "Source is required")}
              id="issue-source"
            >
              <IssueSourceSelector
                value={currentIssueSourceId}
                onValueChange={handleIssueSourceSelect}
                defaultSourceId={issueSourceSettings.projectDefaultSourceId}
                emptyTextVariant="subtle"
                size="default"
                readOnly={!editableHere}
              />
            </PropertyRow>
            )}
      </>
    ),
  };

  const rows = (
    <>
      {typeInHero ? null : (
        <PropertyRow
          icon={PROPERTY_ICON.issueType}
          label="Type"
        >
          <IssueTypeSelector
            buttonVariant="ghost"
            issueTypes={selectableIssueTypes}
            emptyTextVariant="subtle"
            onValueChange={onIssueTypeChange}
            readOnly={!editableHere}
            showChevron={false}
            size="default"
            value={currentIssueTypeId}
            valueMuted
          />
        </PropertyRow>
      )}

      <PropertyRow
        icon={PROPERTY_ICON.workflow}
        label="Workflow"
      >
        <WorkflowSelector
          value={currentWorkflowId}
          onValueChange={(ids) => {
            const next = ids[0];
            if (next) handleWorkflowSelect(next);
          }}
          // In create mode, only list workflows the viewer can create in.
          // (For an existing issue's workflow swap, entry was already gated
          // on viewer access — they're editing within the same scope.)
          editableOnly={isCreateMode}
        >
          <SelectionEntityTrigger
            toEntity={selectionToWorkflowEntity}
            placeholder="Select workflow..."
            emptyTextVariant="subtle"
            size="default"
            title="Workflow"
            // View/comment tier: static value, picker can't open. (Gating must
            // live on the trigger — WorkflowSelector ignores its own `disabled`
            // when children are provided.)
            readOnly={!editableHere}
          />
          {/* The standard picker rung, like Type and Stage beside it. `fit`
              would floor the popup at the row's value slot — the whole
              drawer width in one column — and open a different object. */}
          <EntitySelectorContent>
            <EntitySelectorInput />
            <EntitySelectorPool value="workflows" />
            <EntitySelectorEmpty>No workflows found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </WorkflowSelector>
      </PropertyRow>

      <PropertyRow
        icon={PROPERTY_ICON.stage}
        label="Stage"
      >
        <StageSelector
          workflowId={currentWorkflowId}
          value={currentStageId}
          selectedStage={stage}
          onValueChange={handleStageSelect}
          readOnly={!editableHere}
          emptyTextVariant="subtle"
          size="default"
        />
      </PropertyRow>

      {/* Then the movable rows in the type's order (Source last by default:
          provenance is set once and read rarely). */}
      {propertyOrder.map((key) => (
        <Fragment key={key}>{movableRows[key]}</Fragment>
      ))}
    </>
  );

  // The rows fill a PropertyGrid: two columns once the band's OWN width holds
  // two 360px columns (744px), one column below that. The drawer caps its
  // content at 720px, so only the fullscreen dialog on a wide enough window
  // reaches the second column. The fold control sits under the rows
  // (`SummaryPropertiesFoldRow`).
  return (
    <Box group="box" gap={6} position="relative">
      <PropertyGrid>{rows}</PropertyGrid>
      <SummaryPropertiesFoldRow fold={emptyFold} foldable={foldable} />
    </Box>
  );
}
